// ============================================================================
// firebase.js  (FULL MERGED VERSION) 2025-07-24
// - no template literals in assertRequired
// - EVERY Firestore path goes through safeDoc/safeCollection to avoid undefined.path
// 初始化 + Auth + 通用 + Chat(TTL) + Screenshots/Collections + LinkFlower
// + Playlists/Tracks: ensureAllPlaylist / deletePlaylistKeepTracks / updateTrack / moveTrack / reorderTracks
// ============================================================================

import { initializeApp, getApps, getApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  sendPasswordResetEmail,
} from 'firebase/auth';
import {
  getFirestore,
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  addDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
  Timestamp,
  writeBatch,
  updateDoc,
  runTransaction,
} from 'firebase/firestore';
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { getFunctions } from 'firebase/functions';

/* ────────────────────────────────────────────── Utils */
function assertRequired(name, v) {
  if (v === undefined || v === null || v === '') {
    throw new Error(name + ' is required');
  }
  return v;
}

function _checkSegs(kind, segs) {
  for (let i = 0; i < segs.length; i++) {
    const s = segs[i];
    if (s === undefined || s === null || s === '') {
      throw new Error(kind + ': path segment[' + i + '] is ' + s);
    }
  }
}
function safeDoc() {
  const args = Array.prototype.slice.call(arguments);
  const dbOrRef = args.shift();
  _checkSegs('safeDoc', [dbOrRef].concat(args));
  return doc.apply(null, [dbOrRef].concat(args));
}
function safeCollection() {
  const args = Array.prototype.slice.call(arguments);
  const dbOrRef = args.shift();
  _checkSegs('safeCollection', [dbOrRef].concat(args));
  return collection.apply(null, [dbOrRef].concat(args));
}
function safeAutoDoc(colRef) {
  if (!colRef) throw new Error('safeAutoDoc: colRef is ' + colRef);
  return doc(colRef);
}

/* ────────────────────────────────────────────── Firebase Config */
export const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

/* ────────────────────────────────────────────── Bootstrap */
const defaultApp = getApps().length ? getApp() : initializeApp(firebaseConfig);
export const auth = getAuth(defaultApp);
export const db = getFirestore(defaultApp);
export const storage = getStorage(defaultApp);
export const functions = getFunctions(defaultApp);

// (legacy second app if needed)
if (!getApps().some(a => a.name === 'LexPromptApp')) {
  initializeApp(firebaseConfig, 'LexPromptApp');
}

/* ────────────────────────────────────────────── Auth Helpers */
export const googleProvider = new GoogleAuthProvider();
export const googleSignIn = () => signInWithPopup(auth, googleProvider);
export const googleSignOut = () => signOut(auth);
export const sendPasswordReset = email => sendPasswordResetEmail(auth, email);

/* ────────────────────────────────────────────── Generic User Helpers */
export const userDocRef = (uid = auth.currentUser && auth.currentUser.uid) =>
  uid ? safeDoc(db, 'users', uid) : null;

export async function getBuilderConfig(uid) {
  assertRequired('getBuilderConfig.uid', uid);
  const snap = await getDoc(safeDoc(db, 'users', uid));
  return snap.exists() ? (snap.data().builderConfig || null) : null;
}

export async function saveBuilderConfig(uid, builderConfig) {
  assertRequired('saveBuilderConfig.uid', uid);
  return setDoc(
    safeDoc(db, 'users', uid),
    { builderConfig: { ...builderConfig, updatedAt: serverTimestamp() } },
    { merge: true }
  );
}

export async function loadLcaData(uid = auth.currentUser && auth.currentUser.uid) {
  if (!uid) return null;
  const ref = userDocRef(uid);
  if (!ref) return null;
  const snap = await getDoc(ref);
  const data = snap.exists() ? snap.data() : {};
  return data.lcaData || null;
}

export async function saveLcaData(data, uid = auth.currentUser && auth.currentUser.uid) {
  assertRequired('saveLcaData.uid', uid);
  return setDoc(
    userDocRef(uid),
    { lcaData: { ...data, updatedAt: serverTimestamp() } },
    { merge: true }
  );
}

export async function loadGmailAccounts(uid = auth.currentUser && auth.currentUser.uid) {
  if (!uid) return [];
  const snap = await getDoc(userDocRef(uid));
  return snap.exists() ? (snap.data().gmailAccounts || []) : [];
}

export async function saveGmailAccounts(accounts, uid = auth.currentUser && auth.currentUser.uid) {
  assertRequired('saveGmailAccounts.uid', uid);
  return setDoc(
    userDocRef(uid),
    { gmailAccounts: accounts, updatedAt: serverTimestamp() },
    { merge: true }
  );
}

/* ────────────────────────────────────────────── Generic Upload Helper */
export async function uploadAudio(blob, uidOverride) {
  assertRequired('uploadAudio.blob', blob);
  const uid = uidOverride || (auth.currentUser && auth.currentUser.uid) || 'public';
  const ext = (blob.type.split('/')[1] || 'webm').replace(/[^a-z0-9]/gi, '');
  const filename = Date.now() + '.' + ext;
  const sRef = storageRef(storage, 'lca-audio/' + uid + '/' + filename);
  await uploadBytes(sRef, blob);
  return getDownloadURL(sRef);
}

/* ─────────────────────────────── CHAT helpers (with TTL) ────────────────── */
export async function touchRoom(roomId) {
  assertRequired('touchRoom.roomId', roomId);
  await setDoc(
    safeDoc(db, 'rooms', roomId),
    { lastActive: serverTimestamp(), active: true, ttl: null },
    { merge: true }
  );
}

export async function sendChatMessage(roomId, obj) {
  assertRequired('sendChatMessage.roomId', roomId);
  const sender = obj && obj.sender;
  const text = obj && obj.text;
  const fileUrl = obj && obj.fileUrl;
  const colRef = safeCollection(db, 'rooms', roomId, 'messages');
  await addDoc(colRef, {
    sender: sender,
    text: text || null,
    fileUrl: fileUrl || null,
    createdAt: serverTimestamp(),
    ttl: Timestamp.fromMillis(Date.now() + 60 * 1000),
  });
  await touchRoom(roomId);
}

export function subscribeToChat(roomId, onUpdate) {
  if (!roomId) {
    console.error('subscribeToChat: roomId missing');
    return function () {};
  }
  const qMsgs = query(
    safeCollection(db, 'rooms', roomId, 'messages'),
    orderBy('createdAt')
  );
  return onSnapshot(qMsgs, snap => {
    const msgs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    onUpdate(msgs);
  });
}

export function deleteChatMessage(roomId, mid) {
  assertRequired('deleteChatMessage.roomId', roomId);
  assertRequired('deleteChatMessage.mid', mid);
  return deleteDoc(safeDoc(db, 'rooms', roomId, 'messages', mid));
}

export function watchParticipants(roomId) {
  if (!roomId) {
    console.error('watchParticipants: roomId missing');
    return function () {};
  }
  const colRef = safeCollection(db, 'rooms', roomId, 'participants');
  return onSnapshot(colRef, snap => {
    if (snap.size === 0) {
      setDoc(
        safeDoc(db, 'rooms', roomId),
        { active: false, ttl: Timestamp.fromMillis(Date.now() + 5 * 60 * 60 * 1000) },
        { merge: true }
      );
    }
  });
}

export async function destroyRoom(roomId) {
  assertRequired('destroyRoom.roomId', roomId);
  await setDoc(
    safeDoc(db, 'rooms', roomId),
    { active: false, ttl: Timestamp.now() },
    { merge: true }
  );
  try {
    const batch = writeBatch(db);
    const msgsSnap = await getDocs(safeCollection(db, 'rooms', roomId, 'messages'));
    msgsSnap.forEach(d => batch.delete(d.ref));
    const partsSnap = await getDocs(safeCollection(db, 'rooms', roomId, 'participants'));
    partsSnap.forEach(d => batch.delete(d.ref));
    await batch.commit();
  } catch (e) {
    console.warn('Cascade delete partly failed, TTL will handle rest', e);
  }
}

export async function deleteChatMessageWithFile(roomId, msgId) {
  assertRequired('deleteChatMessageWithFile.roomId', roomId);
  assertRequired('deleteChatMessageWithFile.msgId', msgId);

  const msgRef = safeDoc(db, 'rooms', roomId, 'messages', msgId);
  const snap = await getDoc(msgRef);
  const data = snap.data() || {};

  if (data.fileUrl) {
    try {
      const path = decodeURIComponent(data.fileUrl.split('/o/')[1].split('?')[0]);
      await deleteObject(storageRef(storage, path));
    } catch (e) {
      console.warn('Failed to delete file from Storage:', e);
    }
  }
  await deleteDoc(msgRef);
}

/* ────────────────────────────────────────────────────────────────────────────────
   Screenshots & Collections
   ──────────────────────────────────────────────────────────────────────────────── */
export const screenshotsRef = uid => safeCollection(db, 'users', uid, 'screenshots');

export async function loadScreenshots(uid) {
  assertRequired('loadScreenshots.uid', uid);
  const snap = await getDocs(screenshotsRef(uid));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function addScreenshot(uid, data) {
  assertRequired('addScreenshot.uid', uid);
  const ref = await addDoc(screenshotsRef(uid), { ...data, createdAt: serverTimestamp() });
  const snap = await getDoc(ref);
  return { id: ref.id, ...snap.data() };
}

export const collectionsRef = uid => safeCollection(db, 'users', uid, 'collections');

export async function loadCollections(uid) {
  assertRequired('loadCollections.uid', uid);
  const snap = await getDocs(collectionsRef(uid));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function addCollection(uid, name) {
  assertRequired('addCollection.uid', uid);
  const ref = await addDoc(collectionsRef(uid), {
    name: name,
    ids: [],
    createdAt: serverTimestamp(),
  });
  const snap = await getDoc(ref);
  return { id: ref.id, ...snap.data() };
}

export async function moveToCollection(uid, colId, screenshotIds) {
  assertRequired('moveToCollection.uid', uid);
  assertRequired('moveToCollection.colId', colId);
  const colDoc = safeDoc(db, 'users', uid, 'collections', colId);
  await updateDoc(colDoc, { ids: screenshotIds, updatedAt: serverTimestamp() });
  const snap = await getDoc(colDoc);
  return { id: snap.id, ...snap.data() };
}

export async function uploadBlobFile(blob, path) {
  assertRequired('uploadBlobFile.blob', blob);
  assertRequired('uploadBlobFile.path', path);
  const ext = (blob.type.split('/')[1] || 'bin').replace(/[^a-z0-9]/gi, '');
  const filename = Date.now() + '.' + ext;
  const ref = storageRef(storage, path + '/' + filename);
  await uploadBytes(ref, blob);
  return getDownloadURL(ref);
}

export async function uploadScreenshotFile(uid, file) {
  assertRequired('uploadScreenshotFile.uid', uid);
  assertRequired('uploadScreenshotFile.file', file);
  const ext = (file.type.split('/')[1] || 'bin').replace(/[^a-z0-9]/gi, '');
  const filename = Date.now() + '.' + ext;
  const ref = storageRef(storage, 'users/' + uid + '/screenshots/' + filename);
  await uploadBytes(ref, file);
  return getDownloadURL(ref);
}

/* ────────────────────────────────────────────────────────────────────────────────
   Playlists & Tracks
   ──────────────────────────────────────────────────────────────────────────────── */

export function listenPlaylists(uid, callback) {
  if (!uid) {
    console.error('listenPlaylists: uid is missing');
    return function () {};
  }
  const qPl = query(
    safeCollection(db, 'users', uid, 'playlists'),
    orderBy('order', 'asc')
  );
  return onSnapshot(qPl, snap => {
    const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(list);
  });
}

export function listenTracks(uid, playlistId, callback) {
  if (!uid) {
    console.error('listenTracks: uid is missing');
    return function () {};
  }
  if (!playlistId) {
    console.error('listenTracks: playlistId is missing');
    return function () {};
  }
  const qTr = query(
    safeCollection(db, 'users', uid, 'playlists', playlistId, 'tracks'),
    orderBy('order', 'asc')
  );
  return onSnapshot(qTr, snap => {
    const tracks = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(tracks);
  });
}

export async function addPlaylist(uid, obj) {
  assertRequired('addPlaylist.uid', uid);
  const name = obj && obj.name;
  const slug = obj && obj.slug ? obj.slug : null;
  const col = safeCollection(db, 'users', uid, 'playlists');
  const snap = await getDocs(query(col, orderBy('order', 'desc')));
  const maxOrder = snap.docs.length ? (snap.docs[0].data().order || 0) : -1;
  const ref = await addDoc(col, {
    name: name,
    slug: slug,
    order: maxOrder + 1,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function addTrack(uid, playlistId, trackData) {
  assertRequired('addTrack.uid', uid);
  assertRequired('addTrack.playlistId', playlistId);
  const col = safeCollection(db, 'users', uid, 'playlists', playlistId, 'tracks');
  const snap = await getDocs(query(col, orderBy('order', 'desc')));
  const maxOrder = snap.docs.length ? (snap.docs[0].data().order || 0) : -1;
  const ref = await addDoc(col, {
    ...trackData,
    order: maxOrder + 1,
    addedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export function deleteTrack(uid, playlistId, trackId) {
  assertRequired('deleteTrack.uid', uid);
  assertRequired('deleteTrack.playlistId', playlistId);
  assertRequired('deleteTrack.trackId', trackId);
  return deleteDoc(safeDoc(db, 'users', uid, 'playlists', playlistId, 'tracks', trackId));
}

export function updatePlaylist(uid, playlistId, data) {
  assertRequired('updatePlaylist.uid', uid);
  assertRequired('updatePlaylist.playlistId', playlistId);
  return updateDoc(safeDoc(db, 'users', uid, 'playlists', playlistId), {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

export async function deletePlaylist(uid, playlistId) {
  assertRequired('deletePlaylist.uid', uid);
  assertRequired('deletePlaylist.playlistId', playlistId);
  await deleteDoc(safeDoc(db, 'users', uid, 'playlists', playlistId));
}

export function updateTrack(uid, playlistId, trackId, patch) {
  assertRequired('updateTrack.uid', uid);
  assertRequired('updateTrack.playlistId', playlistId);
  assertRequired('updateTrack.trackId', trackId);
  return updateDoc(
    safeDoc(db, 'users', uid, 'playlists', playlistId, 'tracks', trackId),
    {
      ...patch,
      updatedAt: serverTimestamp(),
    }
  );
}

export async function reorderTracks(uid, playlistId, orderedIds) {
  assertRequired('reorderTracks.uid', uid);
  assertRequired('reorderTracks.playlistId', playlistId);
  if (!Array.isArray(orderedIds)) throw new Error('reorderTracks: orderedIds must be array');

  const batch = writeBatch(db);
  for (let i = 0; i < orderedIds.length; i++) {
    const id = orderedIds[i];
    batch.set(
      safeDoc(db, 'users', uid, 'playlists', playlistId, 'tracks', id),
      { order: i, updatedAt: serverTimestamp() },
      { merge: true }
    );
  }
  await batch.commit();
}

export async function moveTrack(uid, fromPid, toPid, trackId) {
  assertRequired('moveTrack.uid', uid);
  assertRequired('moveTrack.fromPid', fromPid);
  assertRequired('moveTrack.toPid', toPid);
  assertRequired('moveTrack.trackId', trackId);

  const fromRef = safeDoc(db, 'users', uid, 'playlists', fromPid, 'tracks', trackId);
  const snap = await getDoc(fromRef);
  if (!snap.exists()) return;

  const data = snap.data();
  const toCol = safeCollection(db, 'users', uid, 'playlists', toPid, 'tracks');
  const last = await getDocs(query(toCol, orderBy('order', 'desc')));
  const nextOrder = last.empty ? 0 : ((last.docs[0].data().order || 0) + 1);

  const batch = writeBatch(db);
  const newRef = safeAutoDoc(toCol);
  batch.set(newRef, {
    ...data,
    order: nextOrder,
    addedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    fromPlaylistId: fromPid,
    movedAt: serverTimestamp(),
  });
  batch.delete(fromRef);
  await batch.commit();

  return newRef.id;
}



export async function ensureAllPlaylist(uid) {
  assertRequired('ensureAllPlaylist.uid', uid);
  const plsCol = safeCollection(db, 'users', uid, 'playlists');

  // Read with getDocs (transaction.get can't take a Query)
  const snap = await getDocs(query(plsCol, orderBy('order', 'asc')));

  let maxOrder = -1;
  const alls = [];
  snap.docs.forEach(d => {
    const data = d.data();
    maxOrder = Math.max(maxOrder, data.order ?? 0);
    if ((data.name || '').toLowerCase() === 'all') {
      alls.push({ id: d.id, ...data });
    }
  });

  // Exactly one "All" → return it
  if (alls.length === 1) return alls[0].id;

  const batch = writeBatch(db);

  // None → create one
  if (alls.length === 0) {
    const newRef = safeAutoDoc(plsCol);
    batch.set(newRef, {
      name: 'All',
      order: maxOrder + 1,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    await batch.commit();
    return newRef.id;
  }

  // More than one → keep first, delete the rest
  const keep = alls[0];
  for (let i = 1; i < alls.length; i++) {
    batch.delete(safeDoc(db, 'users', uid, 'playlists', alls[i].id));
  }
  await batch.commit();
  return keep.id;
}





export async function deletePlaylistKeepTracks(uid, playlistId, toPlaylistId) {
  assertRequired('deletePlaylistKeepTracks.uid', uid);
  assertRequired('deletePlaylistKeepTracks.playlistId', playlistId);

  const targetPid = toPlaylistId || (await ensureAllPlaylist(uid));

  const fromCol = safeCollection(db, 'users', uid, 'playlists', playlistId, 'tracks');
  const toCol = safeCollection(db, 'users', uid, 'playlists', targetPid, 'tracks');

  const [fromSnap, lastSnap] = await Promise.all([
    getDocs(query(fromCol, orderBy('order', 'asc'))),
    getDocs(query(toCol, orderBy('order', 'desc'))),
  ]);

  let nextOrder = lastSnap.docs.length ? ((lastSnap.docs[0].data().order || 0) + 1) : 0;

  const batch = writeBatch(db);
  fromSnap.docs.forEach(d => {
    const data = d.data();
    const newRef = safeAutoDoc(toCol);
    batch.set(newRef, {
      ...data,
      order: nextOrder++,
      fromPlaylistId: playlistId,
      movedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    batch.delete(d.ref);
  });

  batch.delete(safeDoc(db, 'users', uid, 'playlists', playlistId));
  await batch.commit();

  return targetPid;
}

/* ────────────────────────────────────────────────────────────────────────────────
   LinkFlower
   ──────────────────────────────────────────────────────────────────────────────── */

const lfUserLinksCol = uid => safeCollection(db, 'users', uid, 'links');
const lfUserBannersCol = uid => safeCollection(db, 'users', uid, 'banners');
const lfSlugDoc = slug => safeDoc(db, 'slugs', slug);

export function listenLinkFlowerProfile(uid, callback) {
  if (!uid) {
    console.error('listenLinkFlowerProfile: uid missing');
    return function () {};
  }
  const ref = safeDoc(db, 'users', uid);
  return onSnapshot(ref, snap => {
    if (!snap.exists()) {
      callback(null);
    } else {
      const data = snap.data();
      callback({
        slug: data.slug || '',
        bio: data.bio || '',
        templateIndex: data.templateIndex != null ? data.templateIndex : 0,
        activeBanner: data.activeBanner != null ? data.activeBanner : 0,
      });
    }
  });
}

export async function saveLinkFlowerProfile(uid, partial) {
  assertRequired('saveLinkFlowerProfile.uid', uid);
  return setDoc(
    safeDoc(db, 'users', uid),
    { ...partial, updatedAt: serverTimestamp() },
    { merge: true }
  );
}

export async function updateSlugTransaction(uid, newSlug) {
  assertRequired('updateSlugTransaction.uid', uid);
  newSlug = (newSlug || '').trim().toLowerCase();
  if (!/^[a-z0-9-]{3,30}$/.test(newSlug)) {
    throw new Error('Invalid slug format (3-30 chars, a-z, 0-9, -)');
  }

  await runTransaction(db, async tx => {
    const userRef = safeDoc(db, 'users', uid);
    const userSnap = await tx.get(userRef);
    const prevSlug = userSnap.exists() ? (userSnap.data().slug || null) : null;

    const targetRef = lfSlugDoc(newSlug);
    const targetSnap = await tx.get(targetRef);
    if (targetSnap.exists()) {
      if (targetSnap.data().uid !== uid) throw new Error('Slug already taken');
    } else {
      tx.set(targetRef, { uid: uid, createdAt: serverTimestamp() });
    }

    if (prevSlug && prevSlug !== newSlug) {
      tx.delete(lfSlugDoc(prevSlug));
    }

    tx.set(userRef, { slug: newSlug, updatedAt: serverTimestamp() }, { merge: true });
  });

  return true;
}

export function listenLinks(uid, callback) {
  if (!uid) {
    console.error('listenLinks: uid missing');
    return function () {};
  }
  const qLinks = query(lfUserLinksCol(uid), orderBy('order'));
  return onSnapshot(qLinks, snap => {
    const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(list);
  });
}

export async function addLink(uid, obj) {
  assertRequired('addLink.uid', uid);
  const title = obj && obj.title ? obj.title.trim() : '';
  const url = obj && obj.url ? obj.url.trim() : '';
  if (!title || !url) throw new Error('Title & URL required');
  const snap = await getDocs(lfUserLinksCol(uid));
  const maxOrder = snap.docs.reduce((max, d) => Math.max(max, d.data().order || 0), -1);
  return addDoc(lfUserLinksCol(uid), {
    title: title,
    url: url,
    order: maxOrder + 1,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function updateLink(uid, linkId, patch) {
  assertRequired('updateLink.uid', uid);
  assertRequired('updateLink.linkId', linkId);
  return setDoc(
    safeDoc(db, 'users', uid, 'links', linkId),
    { ...patch, updatedAt: serverTimestamp() },
    { merge: true }
  );
}

export async function deleteLink(uid, linkId) {
  assertRequired('deleteLink.uid', uid);
  assertRequired('deleteLink.linkId', linkId);
  return deleteDoc(safeDoc(db, 'users', uid, 'links', linkId));
}

export async function reorderLinks(uid, orderedIds) {
  assertRequired('reorderLinks.uid', uid);
  if (!Array.isArray(orderedIds)) throw new Error('reorderLinks: orderedIds must be array');
  const batch = writeBatch(db);
  for (let i = 0; i < orderedIds.length; i++) {
    const id = orderedIds[i];
    batch.set(
      safeDoc(db, 'users', uid, 'links', id),
      { order: i, updatedAt: serverTimestamp() },
      { merge: true }
    );
  }
  await batch.commit();
}

export function listenBanners(uid, callback) {
  if (!uid) {
    console.error('listenBanners: uid missing');
    return function () {};
  }
  const qBanners = query(lfUserBannersCol(uid), orderBy('order'));
  return onSnapshot(qBanners, snap => {
    const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(list);
  });
}

export async function uploadBannerFile(uid, file) {
  assertRequired('uploadBannerFile.uid', uid);
  assertRequired('uploadBannerFile.file', file);
  const ext = (file.name.split('.').pop() || 'bin').toLowerCase();
  const path =
    'users/' +
    uid +
    '/banners/' +
    Date.now() +
    '-' +
    Math.random().toString(36).slice(2) +
    '.' +
    ext;
  const ref = storageRef(storage, path);
  await uploadBytes(ref, file);
  const url = await getDownloadURL(ref);

  const snap = await getDocs(lfUserBannersCol(uid));
  const maxOrder = snap.docs.reduce((max, d) => Math.max(max, d.data().order || 0), -1);

  const docRef = await addDoc(lfUserBannersCol(uid), {
    imageUrl: url,
    storagePath: path,
    order: maxOrder + 1,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return { id: docRef.id, imageUrl: url, storagePath: path };
}

export async function addBanner(uid, obj) {
  assertRequired('addBanner.uid', uid);
  assertRequired('addBanner.imageUrl', obj && obj.imageUrl);
  const imageUrl = obj.imageUrl;
  const storagePath = obj.storagePath || null;

  const snap = await getDocs(lfUserBannersCol(uid));
  const maxOrder = snap.docs.reduce((max, d) => Math.max(max, d.data().order || 0), -1);

  return addDoc(lfUserBannersCol(uid), {
    imageUrl: imageUrl,
    storagePath: storagePath,
    order: maxOrder + 1,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function deleteBannerDoc(uid, bannerId) {
  assertRequired('deleteBannerDoc.uid', uid);
  assertRequired('deleteBannerDoc.bannerId', bannerId);
  const ref = safeDoc(db, 'users', uid, 'banners', bannerId);
  const snap = await getDoc(ref);
  const data = snap.exists() ? snap.data() : null;
  await deleteDoc(ref);
  if (data && data.storagePath) {
    try {
      await deleteObject(storageRef(storage, data.storagePath));
    } catch (e) {
      console.warn('Failed to delete banner storage object', e);
    }
  }
}

export async function reorderBanners(uid, orderedIds) {
  assertRequired('reorderBanners.uid', uid);
  if (!Array.isArray(orderedIds)) throw new Error('reorderBanners: orderedIds must be array');
  const batch = writeBatch(db);
  for (let i = 0; i < orderedIds.length; i++) {
    const id = orderedIds[i];
    batch.set(
      safeDoc(db, 'users', uid, 'banners', id),
      { order: i, updatedAt: serverTimestamp() },
      { merge: true }
    );
  }
  await batch.commit();
}

// ============================================================================
// END firebase.js
// ============================================================================
